// TPS Stripe onboarding (#7b/#9) — the servicer firm's self-serve "Payment
// setup" surface. Reads its two Connect accounts' live status and generates a
// FRESH Stripe-hosted onboarding link on demand (robust against link expiry).
// The portal is import-isolated and CANNOT reach @/types/tps, so the wire
// shapes are mirrored LOCALLY here. No React Query in the portal — the tab
// reads with useState and re-runs the loader after a mutation.

import { portalApi } from "./client";

/** One connected account's KYC / capabilities status. `status` is a string
 *  from the backend enum: "Pending" | "Active" | "Restricted" | "Disabled". */
export interface TpsConnectAccountStatus {
  purpose: string;
  stripeAccountId: string;
  status: string;
  capabilitiesJson: string | null;
  onboardedAt: string | null;
}

/** The firm's aggregate Connect status. A `clearing`/`commissions` of `null`
 *  means Phoenix hasn't created that account yet. */
export interface TpsConnectStatus {
  tpsId: string;
  onboardingComplete: boolean;
  onboardingCompletedAt: string | null;
  clearing: TpsConnectAccountStatus | null;
  commissions: TpsConnectAccountStatus | null;
}

/** The result of generating an onboarding link for one account — its id,
 *  derived status, and the Stripe-hosted link (null once Active). */
export interface TpsConnectAccountProvision {
  /** "TpsClearing" | "TpsCommissions". */
  purpose: string;
  stripeAccountId: string;
  /** "Pending" | "Active" | "Restricted" | "Disabled". */
  status: string;
  onboardingUrl: string | null;
  onboardingUrlExpiresAt: string | null;
}

/** Which of the firm's two accounts an onboarding link is for. */
export type TpsConnectAccountKey = "clearing" | "commissions";

export const tpsStripeApi = {
  /** The firm's live Connect onboarding status (re-fetched from Stripe). */
  getStatus: () => portalApi.get<TpsConnectStatus>("/tps/stripe/status"),
  /** Generate a fresh Stripe-hosted onboarding link for one account. */
  onboardingLink: (account: TpsConnectAccountKey) =>
    portalApi.post<TpsConnectAccountProvision>(
      `/tps/stripe/onboarding-link?account=${account}`,
    ),
  /** D2 — mint a Stripe-hosted management link for the ACTIVE Commissions
   *  account (self-serve bank/business edits). Clearing is staff-mediated, so
   *  there is no account parameter. */
  manageLink: () =>
    portalApi.post<TpsConnectAccountProvision>("/tps/stripe/manage-link"),
};
